import { createContext, createElement, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';
import type ViewProductStore from '../../AppData/_stores/ViewProductStore';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import useProductCatalogCategories from './useProductCatalogCategories';
import useSelectedProductCatalogCollection from './useSelectedProductCatalogCollection';

export const PRODUCT_SEARCH_PARAM = 'search';
export const FAVORITES_SEARCH_PARAM = 'favorites';

export type ProductCatalogProductFilter = (product: ViewProductStore) => boolean;

function useProductCatalogFilteringValue(productFilter?: ProductCatalogProductFilter) {
  const params = useParams<{ category1?: string, category2?: string }>();
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const {
    childCategoriesByParentIdAndUrlSlug,
    childrenByParentId,
    topLevelCategoriesByUrlSlug,
  } = useProductCatalogCategories();
  const selectedCollection = useSelectedProductCatalogCollection();
  const viewProductAttributeTypes = useAppSelector(state => state.get('appData').get('viewProductAttributeTypes'));
  const favoriteViewProducts = useAppSelector(state => state.get('userData').get('favoriteViewProducts'));
  const favoritesSelected = searchParams.get(FAVORITES_SEARCH_PARAM) === '1';
  const productSearchQuery = searchParams.get(PRODUCT_SEARCH_PARAM) ?? '';

  const productSearchTerms = useMemo(() => {
    return productSearchQuery
      .trim()
      .toLocaleLowerCase()
      .split(/\s+/)
      .filter(term => term !== '');
  }, [productSearchQuery])

  const activeFiltersByType = useMemo(() => {
    const filtersByType = new Map<number, Set<string>>();

    viewProductAttributeTypes.forEach(type => {
      const filterSearchParam = searchParams.get(type.get('slug'));
      if (filterSearchParam !== null) {
        filtersByType.set(type.get('id'), new Set(filterSearchParam.split(',').filter(filter => filter !== '')));
      }
    });

    return filtersByType;
  }, [searchParams, searchParamsKey, viewProductAttributeTypes])

  const activeAttributeFilterCount = useMemo(() => {
    let count = 0;

    activeFiltersByType.forEach(filters => {
      count += filters.size;
    });

    return count;
  }, [activeFiltersByType])
  const productSearchActive = productSearchQuery.trim() !== '';
  const activeFilterCount = activeAttributeFilterCount
    + (favoritesSelected ? 1 : 0)
    + (productSearchActive ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  const category1 = useMemo(() => {
    return params.category1 !== undefined && selectedCollection === undefined
      ? topLevelCategoriesByUrlSlug.get(params.category1)
      : undefined
  }, [params.category1, selectedCollection, topLevelCategoriesByUrlSlug])

  const category2 = useMemo(() => {
    return params.category2 !== undefined && category1 !== undefined
      ? childCategoriesByParentIdAndUrlSlug.get(category1.get('id'))?.get(params.category2)
      : undefined
  }, [category1, childCategoriesByParentIdAndUrlSlug, params.category2])

  const selectedCategoryIds = useMemo(() => {
    if (!category1) return undefined;
    if (category2) return new Set([category2.get('id')]);

    const categoryIds = new Set([category1.get('id')]);
    (childrenByParentId.get(category1.get('id')) ?? []).forEach(category => {
      categoryIds.add(category.get('id'));
    });

    return categoryIds;
  }, [category1, category2, childrenByParentId])

  const favoriteViewProductIds = useMemo(() => {
    if (!favoritesSelected) return undefined;
    return new Set(favoriteViewProducts.valueSeq().toArray());
  }, [favoriteViewProducts, favoritesSelected])

  const matchesProductBase = useCallback((product: ViewProductStore) => {
    if (productFilter !== undefined && !productFilter(product)) {
      return false;
    }

    if (selectedCollection !== undefined && !selectedCollection.matchesProduct(product)) {
      return false;
    }

    if (selectedCategoryIds !== undefined && !product.get('categories').some(categoryId => selectedCategoryIds.has(categoryId))) {
      return false;
    }

    if (productSearchTerms.length > 0) {
      const searchableText = [
        product.get('name'),
        product.get('description'),
        product.get('slug'),
        product.get('url_slug'),
      ].join(' ').toLocaleLowerCase();

      if (!productSearchTerms.every(term => searchableText.includes(term))) {
        return false;
      }
    }

    return !favoritesSelected || favoriteViewProductIds?.has(product.get('id')) === true;
  }, [favoriteViewProductIds, favoritesSelected, productFilter, productSearchTerms, selectedCategoryIds, selectedCollection])

  const matchesProductAttributeFilters = useCallback((product: ViewProductStore, ignoredTypeId?: number) => {
    for (const [typeId, filters] of activeFiltersByType) {
      if (typeId === ignoredTypeId) continue;

      const productValueId = product.get('listAttributes').get(String(typeId));
      if (productValueId === undefined || !filters.has(String(productValueId))) {
        return false;
      }
    }

    return true;
  }, [activeFiltersByType])

  const matchesProduct = useCallback((product: ViewProductStore) => {
    return matchesProductBase(product) && matchesProductAttributeFilters(product);
  }, [matchesProductAttributeFilters, matchesProductBase])

  return {
    activeAttributeFilterCount,
    activeFilterCount,
    activeFiltersByType,
    category1,
    category2,
    favoritesSelected,
    hasActiveFilters,
    matchesProduct,
    matchesProductAttributeFilters,
    matchesProductBase,
    productSearchQuery,
    productSearchTerms,
    selectedCollection,
    selectedCategoryIds,
  };
}

type ProductCatalogFilteringContextValue = ReturnType<typeof useProductCatalogFilteringValue>;

const ProductCatalogFilteringContext = createContext<ProductCatalogFilteringContextValue | undefined>(undefined);

export function ProductCatalogFilteringProvider(props: {
  children: ReactNode
  productFilter?: ProductCatalogProductFilter
}) {
  const value = useProductCatalogFilteringValue(props.productFilter);

  return createElement(ProductCatalogFilteringContext.Provider, { value }, props.children);
}

export default function useProductCatalogFiltering() {
  const contextValue = useContext(ProductCatalogFilteringContext);
  if (contextValue === undefined) {
    throw new Error('useProductCatalogFiltering must be used within ProductCatalogFilteringProvider');
  }

  return contextValue;
}
